import common from "../../common";
import QuestionModule from "../../maths/modules/QuestionModule";
import QuestionValueStringToFloat from "../../maths/QuestionValueStringToFloat";
import GelButton from "../gel/GelButton";
import GelHackButton from "../gel/GelHackButton";
import GelHackToggleButton from "../gel/GelHackToggleButton";
import QuestionInput from "./QuestionInput";

export default class QuestionInputKeypad extends QuestionInput {
    constructor(question, view) {
        super(question, view);

        this._value = "";
        this._buttons = [];
        this._negative = false;
        this._locked = false;
        this._isKeypadEnabled = true;

        this._panel = new PIXI.Container();
        this._panel.position.set(0, -200);
        this.addChild(this._panel);

        this._grid = [
            {value: 1,      x: -1,  y: -1,  toggle: false},
            {value: 2,      x: 0,   y: -1,  toggle: false},
            {value: 3,      x: 1,   y: -1,  toggle: false},
            {value: 4,      x: -1,  y: 0,   toggle: false},
            {value: 5,      x: 0,   y: 0,   toggle: false},
            {value: 6,      x: 1,   y: 0,   toggle: false},
            {value: 7,      x: -1,  y: 1,   toggle: false},
            {value: 8,      x: 0,   y: 1,   toggle: false},
            {value: 9,      x: 1,   y: 1,   toggle: false},
            {value: "del",  x: -2,  y: 2,   toggle: false},
            {value: ".",    x: -1,  y: 2,   toggle: false},
            {value: 0,      x: 0,   y: 2,   toggle: false},
            {value: "-",    x: 1,   y: 2,   toggle: false}
        ];
        this._spacing = new PIXI.Point(96, 76);

        this.createKeypad();

        this._onInputKeyDownBinding = P3.input.signals.keyDown.add(this.onInputKeyDown, this);
    };

    init() {
        super.init();

        this.update(0);
    }

    destroy() {
        P3.input.signals.keyDown.detach(this._onInputKeyDownBinding, this);

        super.destroy();
    }

    update(dt) {
        super.update(dt);

        const user = common.save.getUserData(common.userId);
        if (this._isKeypadEnabled && !user.showKeypad) {
            this.setInteractive(false);
            this._isKeypadEnabled = false;

            console.log("disable keypad");
        } else if (!this._isKeypadEnabled && user.showKeypad) {
            this._isKeypadEnabled = true;
            this.setInteractive(true);
            console.log("enabled keypad");
        }

        this._panel.visible = user.showKeypad;
        if (this._tutorialHandRunning && this._question.module === QuestionModule.MODULE_TUTORIAL)
            this._tutorialHand.visible = this._panel.visible;
    }

    lock() {
        this._locked = true;
        for (let i = 0; i < this._buttons.length; ++i) {
            const button = this._buttons[i];
            button.enabled = false;
        }
    }

    unlock() {
        this._locked = false;
        for (let i = 0; i < this._buttons.length; ++i) {
            const button = this._buttons[i];
            button.enabled = true;
        }
    }

    setInteractive(value) {
        if (!this._isKeypadEnabled) return;

        for (let i = 0; i < this._buttons.length; ++i) {
            const button = this._buttons[i];
            button.interactive = value;
        }
    }

    createKeypad() {
        for (let i = 0; i < this._grid.length; ++i) {
            const t = this._grid[i];
            const states = new P3.ButtonStates();
            states.normal = P3.assets.texture("ui_calculator_btn_normal");
            states.over = P3.assets.texture("ui_calculator_btn_over");
            states.down = P3.assets.texture("ui_calculator_btn_down");
            states.icon = t.value === "del" ? P3.assets.texture("ui_calculator_backspace") : null;
            states.normalAlt = P3.assets.texture("ui_calculator_btn_over");

            let button;
            if (!PIXI.utils.isMobile.any) {
                button = t.toggle ? new GelHackToggleButton(states) : new GelHackButton(states);
            } else {
                button = t.toggle ? new P3.ToggleButton(states) : new P3.Button(states);
            }
            button.value = t.value;
            button.position.set(t.x * this._spacing.x, t.y * this._spacing.y);
            button.animate = false;
            button.animateIcon = false;
            button.tabIndex = GelButton.TAB_INDEX_LEVEL_1 + i;
            button.signals.click.add(this.onKeypadButtonClick, this);
            this._panel.addChild(button);
            this._buttons.push(button);

            button.overSoundName = "sfx_btn_keypad_rollover_00";
            button.clickSoundName = "sfx_btn_keypad_press_00";

            if (t.value !== "del") {
                button.label = new PIXI.Text(button.value.toString(), {
                    fontSize: 40, fontFamily: "BBC Reith Sans", fontWeight: "bold", fill: 0xFFFFFF});
                button.label.position.set(0, -4);
                button.label.anchor.set(0.5);
                button.label.resolution = 2;
                button.animation.addChild(button.label);
            }
        }
    }

    initTutorial() {
        super.initTutorial();

        const firstDigit = parseInt(this._question.answer.toString().charAt(0));

        let button;
        for (let i = 0; i < this._buttons.length; ++i) {
            button = this._buttons[i];
            if (button.value === firstDigit) break;
        }

        const localPosition = this._tutorialHand.parent.toLocal(button.position, button.parent);

        if (button) {
            this._tutorialHandStart = new PIXI.Point(localPosition.x + 40, localPosition.y + 240);
            this._tutorialHandAnchor = new PIXI.Point(localPosition.x + 40, localPosition.y + 40);
            this._tutorialHandEnd = new PIXI.Point(localPosition.x, localPosition.y - 12);
        }
    }

    animateTutorial() {
        this._tutorialHandTweens.removeAll();

        const a = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .delay(200)
            .onStart(() => {
                this._tutorialHand.position.set(this._tutorialHandStart.x, this._tutorialHandStart.y);
                this._tutorialHand.alpha = 0;
            });

        const b = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .to({x: [this._tutorialHandAnchor.x, this._tutorialHandEnd.x], y: [this._tutorialHandAnchor.y, this._tutorialHandEnd.y], alpha: 1}, 640)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .interpolation(P3.utils.TWEEN.Interpolation.Bezier)
            .onStart(() => {
                this._tutorialHand.visible = true;
            })
            .onComplete(() => {
                this._tutorialHand.gotoAndPlay("tap");
            });

        const c = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .to({alpha: 0}, 240)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(800)
            .onComplete(() => {
                this._tutorialHand.visible = false;
                this.animateTutorial();
            });

        a.chain(b);
        b.chain(c);
        a.start();
    }

    onKeypadButtonClick(button) {
        if (button.value === "del") {
            if (this._value.length > 0)
                this._value = this._value.substr(0, this._value.length - 1);
        } else if (this._value.length < 12) {
            this._value += button.value;
        }
        this._view.setAnswer(this._value.length > 0 ? new QuestionValueStringToFloat((this._negative ? "-" : "") + this._value) : null);
        console.log(this._view.answer);
    }

    onInputKeyDown(event) {
        event.key === "Backspace" && event.preventDefault();

        if (!this._locked) {
            if (event.key === "Backspace" || event.key === "Delete") {
                if (this._value.length > 0)
                    this._value = this._value.substr(0, this._value.length - 1);
            } else if (["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "-"].indexOf(event.key) !== -1 && this._value.length < 12) {
                this._value += event.key;
            }
            this._view.setAnswer(this._value.length > 0 ? new QuestionValueStringToFloat((this._negative ? "-" : "") + this._value) : null);
            console.log(this._view.answer);
        }

        if (event.key === "Enter" && this._view.confirmButton.enabled) {
            this._view.onConfirmButtonClick();
        }
    }

    get value() {
        return parseInt(this._value);
    }
}